import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Send } from "lucide-react";
import {
  leadFormSchema,
  type LeadFormValues,
  REGION_OPTIONS,
  CLIENT_TYPE_OPTIONS,
  STAGE_OPTIONS,
  DEADLINE_OPTIONS,
  QUANTITY_OPTIONS,
} from "@/lib/leadSchema";
import { captureUtm } from "@/lib/leadUtils";
import { trackSolariFormSubmit, trackCtaClick } from "@/lib/gtm";
import { useToast } from "@/hooks/use-toast";

const empty: LeadFormValues = {
  fullName: "",
  phone: "",
  email: "",
  regionGoiania: "Sim" as never,
  clientType: "Sou construtor(a)" as never,
  constructionStage: "Inicial" as never,
  deadline: "Imediato" as never,
  quantity: "1 a 5" as never,
  message: "",
};

const HeroLeadForm = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [values, setValues] = useState<LeadFormValues>({ ...empty, regionGoiania: "" as never, clientType: "" as never, constructionStage: "" as never, deadline: "" as never, quantity: "" as never });
  const [errors, setErrors] = useState<Partial<Record<keyof LeadFormValues, string>>>({});
  const [submitting, setSubmitting] = useState(false);

  const set = <K extends keyof LeadFormValues>(k: K, v: LeadFormValues[K]) => {
    setValues((p) => ({ ...p, [k]: v }));
    if (errors[k]) setErrors((p) => ({ ...p, [k]: undefined }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    trackCtaClick("Quero receber meu orçamento");
    const parsed = leadFormSchema.safeParse(values);
    if (!parsed.success) {
      const fe: Partial<Record<keyof LeadFormValues, string>> = {};
      parsed.error.errors.forEach((er) => {
        if (er.path[0]) fe[er.path[0] as keyof LeadFormValues] = er.message;
      });
      setErrors(fe);
      return;
    }
    setSubmitting(true);
    const utm = captureUtm();
    try {
      const response = await fetch("/api/public/leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...parsed.data, ...utm, source: "landing_page", action: "create" }),
      });
      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.ok) {
        throw new Error(data?.message || data?.error || "Falha");
      }
      trackSolariFormSubmit({
        clientType: parsed.data.clientType,
        regionGoiania: parsed.data.regionGoiania,
        constructionStage: parsed.data.constructionStage,
        deadline: parsed.data.deadline,
        quantity: parsed.data.quantity,
      });
      navigate("/obrigado");
    } catch (err) {
      console.error(err);
      toast({
        title: "Não foi possível enviar",
        description: "Verifique as informações e tente novamente.",
        variant: "destructive",
      });
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="rounded-2xl bg-card p-5 shadow-2xl md:p-6">
      <h3 className="mb-1 font-heading text-lg font-bold text-foreground md:text-xl">
        Receba seu orçamento gratuito
      </h3>
      <p className="mb-4 text-sm text-muted-foreground">Preencha em 1 minuto. Resposta rápida.</p>

      <div className="grid gap-3">
        <div>
          <Label htmlFor="fullName" className="text-xs font-medium">Nome *</Label>
          <Input id="fullName" value={values.fullName} onChange={(e) => set("fullName", e.target.value)} className={errors.fullName ? "border-destructive" : ""} />
          {errors.fullName && <p className="mt-1 text-xs text-destructive">{errors.fullName}</p>}
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label htmlFor="phone" className="text-xs font-medium">Telefone / WhatsApp *</Label>
            <Input id="phone" type="tel" placeholder="(62) 99999-9999" value={values.phone} onChange={(e) => set("phone", e.target.value)} className={errors.phone ? "border-destructive" : ""} />
            {errors.phone && <p className="mt-1 text-xs text-destructive">{errors.phone}</p>}
          </div>
          <div>
            <Label htmlFor="email" className="text-xs font-medium">E-mail *</Label>
            <Input id="email" type="email" value={values.email} onChange={(e) => set("email", e.target.value)} className={errors.email ? "border-destructive" : ""} />
            {errors.email && <p className="mt-1 text-xs text-destructive">{errors.email}</p>}
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label className="text-xs font-medium">Goiânia ou Região? *</Label>
            <Select value={values.regionGoiania} onValueChange={(v) => set("regionGoiania", v as LeadFormValues["regionGoiania"])}>
              <SelectTrigger className={errors.regionGoiania ? "border-destructive" : ""}><SelectValue placeholder="Selecione" /></SelectTrigger>
              <SelectContent>{REGION_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
            {errors.regionGoiania && <p className="mt-1 text-xs text-destructive">{errors.regionGoiania}</p>}
          </div>
          <div>
            <Label className="text-xs font-medium">Você é... *</Label>
            <Select value={values.clientType} onValueChange={(v) => set("clientType", v as LeadFormValues["clientType"])}>
              <SelectTrigger className={errors.clientType ? "border-destructive" : ""}><SelectValue placeholder="Selecione" /></SelectTrigger>
              <SelectContent>{CLIENT_TYPE_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
            {errors.clientType && <p className="mt-1 text-xs text-destructive">{errors.clientType}</p>}
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label className="text-xs font-medium">Estágio da obra *</Label>
            <Select value={values.constructionStage} onValueChange={(v) => set("constructionStage", v as LeadFormValues["constructionStage"])}>
              <SelectTrigger className={errors.constructionStage ? "border-destructive" : ""}><SelectValue placeholder="Selecione" /></SelectTrigger>
              <SelectContent>{STAGE_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
            {errors.constructionStage && <p className="mt-1 text-xs text-destructive">{errors.constructionStage}</p>}
          </div>
          <div>
            <Label className="text-xs font-medium">Prazo *</Label>
            <Select value={values.deadline} onValueChange={(v) => set("deadline", v as LeadFormValues["deadline"])}>
              <SelectTrigger className={errors.deadline ? "border-destructive" : ""}><SelectValue placeholder="Selecione" /></SelectTrigger>
              <SelectContent>{DEADLINE_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
            {errors.deadline && <p className="mt-1 text-xs text-destructive">{errors.deadline}</p>}
          </div>
        </div>

        <div>
          <Label className="text-xs font-medium">Quantidade de portas *</Label>
          <Select value={values.quantity} onValueChange={(v) => set("quantity", v as LeadFormValues["quantity"])}>
            <SelectTrigger className={errors.quantity ? "border-destructive" : ""}><SelectValue placeholder="Selecione" /></SelectTrigger>
            <SelectContent>{QUANTITY_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
          </Select>
          {errors.quantity && <p className="mt-1 text-xs text-destructive">{errors.quantity}</p>}
        </div>

        <div>
          <Label htmlFor="message" className="text-xs font-medium">Mensagem (opcional)</Label>
          <Textarea id="message" rows={2} placeholder="Conte brevemente o que você precisa para sua obra." value={values.message} onChange={(e) => set("message", e.target.value)} />
        </div>

        <Button type="submit" disabled={submitting} className="mt-1 w-full bg-primary py-6 font-heading text-base font-bold text-primary-foreground hover:bg-primary/90">
          {submitting ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Enviando...</>) : (<><Send className="mr-2 h-4 w-4" /> Quero receber meu orçamento</>)}
        </Button>
        <p className="text-center text-[11px] text-muted-foreground">Seus dados estão seguros. Não compartilhamos com terceiros.</p>
      </div>
    </form>
  );
};

export default HeroLeadForm;
